import { useQuery } from "@tanstack/react-query";
import { Button } from "antd";
import { useMemo, useState } from "react";
import ReportingExportButton from "../../../components/Button/ReportingExportButton";
import AuditLogModal from "../../../components/Modal/AuditLogModal";
import NonConformancePDFTemplate from "../../../components/PDF/Reporting/non-conformance/NonConformancePDFTemplate";
import QueryRetry from "../../../components/QueryRetry";
import ReportItemsPanel from "../../../components/Reporting/items/ReportItemsPanel";
import ReportCreatedLine from "../../../components/Reporting/ReportCreatedLine";
import {
	getAssetCard,
	getProjectFacts,
	getReviewerFact,
	getTenantFact,
	LookedUpValue,
} from "../../../components/Reporting/reportFacts";
import ReportHeader, {
	ReportHeaderFact,
} from "../../../components/Reporting/ReportHeader";
import { ReportPotentialRisks } from "../../../components/Reporting/ReportRisks";
import { useGlobalLoading } from "../../../hooks/useGlobalLoading";
import { useLookup } from "../../../hooks/useLookup";
import useReportDocumentsDrawer from "../../../hooks/useReportDocumentsDrawer";
import useReportEditor from "../../../hooks/useReportEditor";
import useReportItemNames from "../../../hooks/useReportItemNames";
import { getNonConformanceList } from "../../../services/api-server/non_conformance";
import { GetAntIcon } from "../../../utils/ant_icons";
import { formatDateDdMmmYyyy } from "../../../utils/date_formatter";
import { createNonConformanceLookupConfig } from "../../../utils/reporting/lookupConfigs/non-conformance";
import { getNcrTypeLabel } from "../../../utils/reporting/nonConformance";
import { getReportAuthorEmail } from "../../../utils/reporting/reportAuthor";
import { REPORT_ITEM_KINDS } from "../../../utils/reporting/reportItemKinds";
import { getReportStatusDisplay } from "../../../utils/reporting/statusDisplay";
import { handleExportReport } from "../../../utils/reports";

type NonConformanceOverviewProps = {
	reportId: string;
};

const ACTIONS = REPORT_ITEM_KINDS.follow_up_actions;

function NonConformanceOverview({
	reportId,
}: Readonly<NonConformanceOverviewProps>) {
	const [auditLogOpen, setAuditLogOpen] = useState(false);

	const { data, isLoading, isError } = useQuery({
		queryKey: ["report-overview", reportId],
		queryFn: async () => {
			const response: any = await getNonConformanceList({ _id: reportId });
			return response[0] ?? null;
		},
		enabled: !!reportId,
	});

	const editor = useReportEditor("non_conformance", data, [
		"report-overview",
		reportId,
	]);
	const documents = useReportDocumentsDrawer("non_conformance", reportId, data);

	useGlobalLoading("ncr-overview", isLoading);

	const isRejected = data?.is_rejected === true;
	const statusDisplay = getReportStatusDisplay(
		data?.status,
		data?.open_status,
		isRejected
	);

	const lookupConfig = useMemo(
		() => createNonConformanceLookupConfig([data?.operator]),
		[data?.operator]
	);

	const { dataWithLookup, isLoading: isLoadingLookup } = useLookup(
		["lookup", "report-overview", reportId],
		data ? [data] : [],
		lookupConfig,
		0
	);
	const lookupData = dataWithLookup[0];

	// From the looked-up report, so the export keeps the department, author and
	// reviewer names the PDF prints.
	const { emailToName, reportWithNames, isLoadingNames } = useReportItemNames(
		["report-actions-responsible", reportId],
		lookupData,
		ACTIONS
	);

	if (isError) {
		// The drawers stay, so a failed background refetch doesn't close them.
		return (
			<>
				<QueryRetry queryKey={["report-overview", reportId]} />
				{documents.drawer}
				{editor.drawer}
			</>
		);
	}

	const facts: ReportHeaderFact[] = [
		{
			key: "date_expired",
			label: "Date expiry",
			value: formatDateDdMmmYyyy(data?.date_expired),
		},
		{
			key: "department",
			label: "Department",
			value: (
				<LookedUpValue
					value={lookupData?.department_name}
					isLoading={isLoadingLookup}
				/>
			),
		},
		{
			key: "ncr_type",
			label: "NCR type",
			value: getNcrTypeLabel(data?.ncr_type),
		},
		...getProjectFacts(lookupData, isLoadingLookup),
		getReviewerFact(lookupData, isLoadingLookup),
		getTenantFact(data?.operator),
	];

	const created = (
		<ReportCreatedLine
			submittedOn={data?.submitted_on}
			authorName={lookupData?.author_name}
			authorEmail={getReportAuthorEmail(data)}
			historyTitle="NCR review history"
			historyData={lookupData ?? data}
			onOpenAuditLog={() => setAuditLogOpen(true)}
		/>
	);

	return (
		<>
			{data && (
				// Keyed by report, like the actions panel: another NCR opened in
				// place starts expanded, with its summaries clamped.
				<ReportHeader
					key={reportId}
					label="Non-conformance report details"
					reportNumber={data.report_number}
					title={data.title}
					status={statusDisplay}
					actions={
						<>
							<Button icon={GetAntIcon("file2")} onClick={documents.open}>
								Additional documents
							</Button>
							<ReportingExportButton
								type="default"
								onExport={() =>
									handleExportReport(
										reportWithNames,
										"non_conformance",
										NonConformancePDFTemplate
									)
								}
								isLoading={isLoadingLookup || isLoadingNames}
							/>
						</>
					}
					created={created}
					facts={facts}
					media={getAssetCard(lookupData, isLoadingLookup)}
					summaries={[
						{
							key: "requirements",
							label: "Requirement",
							text: data.requirements,
						},
						{
							key: "description",
							label: "Brief description",
							text: data.description,
						},
					]}
					sections={
						<ReportPotentialRisks
							risks={data.potential_risks}
							operator={data.operator}
						/>
					}
				>
					{/* Keyed by report, so another NCR opened in place starts with
					    its own filters and open rows. */}
					<ReportItemsPanel
						key={reportId}
						kind={ACTIONS}
						items={reportWithNames?.follow_up_actions ?? []}
						emailToName={emailToName}
						report={data}
						canEditItem={editor.canEditItem}
						onEditItem={editor.openItem}
						onAddItems={editor.canEdit ? editor.open : undefined}
					/>
				</ReportHeader>
			)}
			{/* Outside the header, so collapsing it never closes the log. */}
			{data && (
				<AuditLogModal
					auditlogFilter={{ level: "non_conformance", level_id: data._id }}
					label="Non-conformance report"
					type="reporting"
					open={auditLogOpen}
					handleClose={() => {
						setAuditLogOpen(false);
					}}
				/>
			)}
			{documents.drawer}
			{editor.drawer}
		</>
	);
}

export default NonConformanceOverview;
